import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import axios from 'axios';
import NotificationBell from './NotificationBell';
import PushPermissionPrompt from './PushPermissionPrompt';
import UserAvatar from './UserAvatar';
import BrandLogo from './BrandLogo';
import { APP_SHORT_NAME, APP_TAGLINE } from '../config/appConfig';
import {
  LayoutDashboard,
  CheckSquare,
  Kanban,
  Table,
  Code,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Share2,
  ClipboardCheck,
  Palette,
  PanelLeftClose,
  PanelLeft,
  UserCircle,
  History,
  ShieldCheck,
  StickyNote,
  Tag,
  Layers,
  ListChecks,
} from 'lucide-react';

const NavLink = ({ item, sidebarCollapsed, location, setSidebarOpen, pendingApprovalCount, socialMediaAttentionCount }) => {
  const isActive = location.pathname === item.path;
  const Icon = item.icon;
  const badgeCount = item.path === '/manager-approval'
    ? pendingApprovalCount
    : item.path === '/social-media'
      ? socialMediaAttentionCount
      : 0;
  const badgeTestId = item.path === '/social-media' ? 'nav-social-media-attention-badge' : 'nav-pending-approval-badge';
  const showBadge = badgeCount > 0;
  const badgeLabel = badgeCount > 99 ? '99+' : String(badgeCount);

  if (sidebarCollapsed) {
    return (
      <Link
        to={item.path}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => setSidebarOpen(false)}
        title={showBadge ? `${item.label} (${badgeCount} pending)` : item.label}
        aria-label={showBadge ? `${item.label}, ${badgeCount} ${item.path === '/social-media' ? 'posts need attention today' : 'pending approvals'}` : item.label}
        className={`relative group mx-auto flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
          isActive
            ? 'bg-brand-navy text-white shadow-sm shadow-brand-navy/30'
            : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
        }`}
      >
        {isActive && (
          <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-brand-accent" />
        )}
        <Icon size={18} strokeWidth={isActive ? 2.25 : 2} />
        {showBadge && (
          <span
            data-testid={badgeTestId}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none ring-2 ring-white"
          >
            {badgeLabel}
          </span>
        )}
      </Link>
    );
  }

  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`relative flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
        isActive
          ? 'bg-brand-navy text-white shadow-md shadow-brand-navy/20'
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      }`}
      onClick={() => setSidebarOpen(false)}
    >
      <Icon size={18} strokeWidth={isActive ? 2.25 : 2} />
      <span className="font-medium text-sm">{item.label}</span>
      {showBadge && (
        <span
          data-testid={badgeTestId}
          className={`ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[11px] font-bold leading-none ${
            isActive ? 'bg-brand-lime text-brand-navy' : 'bg-red-500 text-white'
          }`}
        >
          {badgeLabel}
        </span>
      )}
    </Link>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { isManager, hasPageAccess, rbacRoleDisplayName } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
    } catch {
      // ignore storage errors (private mode, quota, etc.)
    }
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Notification click while the service worker fired a push and the app tab
  // is already open — it posts the deep link back so we navigate in-SPA
  // instead of a full page load.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'notification-click' && event.data.url) {
        const url = new URL(event.data.url);
        navigate(url.pathname + url.search);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  // Every nav item is now controlled by a single `page_access` key which comes
  // from the RBAC role assigned to the user. `User Types Management` edits the
  // same map; `Permission Matrix` edits granular perms of the same role.
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permissionKey: 'dashboard' },
    { path: '/my-day', icon: CheckSquare, label: 'My Day', permissionKey: 'my_day' },
    { path: '/kanban', icon: Kanban, label: 'Kanban Board', permissionKey: 'kanban_board' },
    { path: '/tasks', icon: Table, label: 'Task Table', permissionKey: 'task_table' },
    { path: '/developer', icon: Code, label: 'Developer Queue', permissionKey: 'developer_queue' },
    { path: '/design-queue', icon: Palette, label: 'UI/UX Design Queue', permissionKey: 'uiux_queue' },
    { path: '/manager-approval', icon: ClipboardCheck, label: 'Manager Approval', permissionKey: 'manager_approval' },
    { path: '/social-media', icon: Share2, label: 'Social Media', permissionKey: 'social_media' },
    { path: '/notes', icon: StickyNote, label: 'Sticky Notes', permissionKey: null },
    { path: '/users', icon: Users, label: 'Users', permissionKey: 'users' },
    { path: '/user-types', icon: Users, label: 'User Types', permissionKey: 'user_types' },
    { path: '/admin/brands', icon: Tag, label: 'Brand Management', permissionKey: 'settings' },
    { path: '/admin/social-media-options', icon: Layers, label: 'SM Options', permissionKey: 'settings' },
    { path: '/admin/task-options', icon: ListChecks, label: 'Task Options', permissionKey: 'settings' },
    { path: '/admin/permissions', icon: ShieldCheck, label: 'Permission Matrix', permissionKey: 'settings' },
    { path: '/activity-log', icon: History, label: 'Activity Log', permissionKey: 'activity_log' },
    { path: '/profile', icon: UserCircle, label: 'My Profile', permissionKey: null },
    { path: '/reports', icon: BarChart3, label: 'Reports', permissionKey: 'reports' },
    { path: '/settings', icon: Settings, label: 'Settings', permissionKey: 'settings' },
    { path: '/rbac-debug', icon: ShieldCheck, label: 'RBAC Debug', permissionKey: 'settings' },
  ];

  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);
  const [socialMediaAttentionCount, setSocialMediaAttentionCount] = useState(0);

  useEffect(() => {
    if (!isManager) {
      setPendingApprovalCount(0);
      return;
    }
    let cancelled = false;
    const loadPendingCount = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/pending-approval`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
          },
          params: { _ts: Date.now() },
        });
        if (!cancelled) setPendingApprovalCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch (error) {
        if (!cancelled) setPendingApprovalCount(0);
      }
    };
    loadPendingCount();
    const interval = setInterval(loadPendingCount, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [isManager, location.pathname]);

  // Same attention-count endpoint/logic that backs the Social Media > Posts
  // tab badge — reused here so both badges always agree.
  useEffect(() => {
    if (!isManager && !hasPageAccess('social_media')) {
      setSocialMediaAttentionCount(0);
      return;
    }
    let cancelled = false;
    const loadAttentionCount = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/social-media/posts/attention-count`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache',
          },
          params: { _ts: Date.now() },
        });
        if (!cancelled) setSocialMediaAttentionCount(res.data?.count || 0);
      } catch (error) {
        if (!cancelled) setSocialMediaAttentionCount(0);
      }
    };
    loadAttentionCount();
    window.addEventListener('social-media-posts-changed', loadAttentionCount);
    const interval = setInterval(loadAttentionCount, 60000);
    const now = new Date();
    const msUntilMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5) - now;
    const midnightTimer = setTimeout(loadAttentionCount, msUntilMidnight);
    return () => {
      cancelled = true;
      window.removeEventListener('social-media-posts-changed', loadAttentionCount);
      clearInterval(interval);
      clearTimeout(midnightTimer);
    };
  }, [isManager, hasPageAccess]);

  const filteredNavItems = navItems.filter((item) => {
    // Always-available items (no permissionKey means every logged-in user)
    if (!item.permissionKey) return true;
    if (isManager) return true;
    return hasPageAccess(item.permissionKey);
  });

  return (
    <div className="app-shell min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandLogo size={32} className="rounded-lg shadow-sm" />
            <h1 className="text-xl font-bold bg-gradient-to-r from-brand-navy to-brand-accent bg-clip-text text-transparent">
              Clarity {APP_SHORT_NAME}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-slate-100"
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-white border-r border-slate-200 z-50 transition-[width] duration-300 ease-in-out flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${sidebarCollapsed ? 'w-16' : 'w-64'}`}
      >
        {/* Sidebar Header with Toggle */}
        <div
          className={`flex-shrink-0 border-b border-slate-200 ${
            sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'
          }`}
        >
          <div className={`flex items-center ${sidebarCollapsed ? 'flex-col gap-2' : 'justify-between gap-2'}`}>
            {sidebarCollapsed ? (
              <BrandLogo size={30} className="rounded-lg shadow-sm" />
            ) : (
              <div className="flex items-center gap-2 min-w-0">
                <BrandLogo size={32} className="rounded-lg shadow-sm flex-shrink-0" />
                <div className="min-w-0">
                  <h1 className="text-lg font-bold bg-gradient-to-r from-brand-navy to-brand-accent bg-clip-text text-transparent leading-tight whitespace-nowrap">
                    Clarity {APP_SHORT_NAME}
                  </h1>
                  <p className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{APP_TAGLINE}</p>
                </div>
              </div>
            )}
            {/* Desktop Sidebar Toggle Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors"
              data-testid="sidebar-toggle"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
        </div>

        {/* User Info */}
        <div
          className={`flex-shrink-0 border-b border-slate-200 ${
            sidebarCollapsed ? 'px-2 py-3' : 'px-4 py-4'
          }`}
        >
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div title={`${user?.full_name || ''} · ${rbacRoleDisplayName || user?.role_level || ''}`}>
                <UserAvatar user={user} size="md" />
              </div>
              <div className="hidden lg:block">
                <NotificationBell />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size="lg" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate text-sm" data-testid="user-display-name">{user?.full_name}</p>
                <p className="text-xs text-slate-500 truncate">{rbacRoleDisplayName || user?.role_level}</p>
              </div>
              <div className="hidden lg:block flex-shrink-0">
                <NotificationBell />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav
          className={`sidebar-scroll flex-1 overflow-y-auto py-3 ${
            sidebarCollapsed ? 'px-2 space-y-1' : 'px-3 space-y-1'
          }`}
        >
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              sidebarCollapsed={sidebarCollapsed}
              location={location}
              setSidebarOpen={setSidebarOpen}
              pendingApprovalCount={pendingApprovalCount}
              socialMediaAttentionCount={socialMediaAttentionCount}
            />
          ))}
        </nav>

        {/* Logout Button */}
        <div
          className={`flex-shrink-0 border-t border-slate-200 bg-white ${
            sidebarCollapsed ? 'px-2 py-3' : 'px-3 py-3'
          }`}
        >
          {sidebarCollapsed ? (
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              title="Logout"
              aria-label="Logout"
              className="mx-auto flex items-center justify-center w-10 h-10 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut size={18} />
            </button>
          ) : (
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-red-600 hover:bg-red-50 w-full transition-colors"
            >
              <LogOut size={18} />
              <span className="font-medium text-sm">Logout</span>
            </button>
          )}
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`pt-16 lg:pt-0 transition-[margin] duration-300 ease-in-out ${
        sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      <PushPermissionPrompt />
    </div>
  );
};

export default Layout;